// Keycloak initialization and context
import Keycloak from 'keycloak-js';

let keycloakInstance: any = null;
let isInitializing = false; // Prevent concurrent initialization attempts
let initializationFailed = false; // Track if initialization has permanently failed

// Load attempted OAuth codes from sessionStorage
const loadAttemptedCodes = (): Set<string> => {
  try {
    const stored = sessionStorage.getItem('attempted_oauth_codes');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

// Save attempted OAuth codes to sessionStorage
const saveAttemptedCodes = (codes: Set<string>): void => {
  try {
    sessionStorage.setItem('attempted_oauth_codes', JSON.stringify(Array.from(codes)));
  } catch {
    // Ignore storage errors
  }
};

let attemptedOAuthCodes = loadAttemptedCodes();

// Immediately track any OAuth code in the URL when the module loads
(() => {
  console.log('🏁 Keycloak module loading...');
  console.log('🔍 Current URL:', window.location.href);
  console.log('🔍 Search params:', window.location.search);
  console.log('🔍 Hash:', window.location.hash);

  // Keycloak can put OAuth parameters in either query string OR hash fragment
  // Check both to be safe
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1)); // Remove leading #

  const oauthCode = urlParams.get('code') || hashParams.get('code');
  const sessionState = urlParams.get('session_state') || hashParams.get('session_state');

  console.log('🔍 OAuth code in URL:', oauthCode ? oauthCode.substring(0, 8) + '...' : 'NONE');
  console.log('🔍 Session state:', sessionState ? sessionState.substring(0, 8) + '...' : 'NONE');
  console.log('🔍 Previously attempted codes:', Array.from(attemptedOAuthCodes).map(c => c.substring(0, 8) + '...'));

  if (oauthCode) {
    console.log('🔍 Module load: Found OAuth code in URL:', oauthCode.substring(0, 8) + '...');

    // Check if we've attempted this exact OAuth code before
    if (attemptedOAuthCodes.has(oauthCode)) {
      console.error('❌ Module load: This OAuth code was already attempted!');
      console.error('❌ BLOCKING REUSE - cleaning URL and preventing init');
      // Clean up immediately - remove from both query string and hash
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      url.searchParams.delete('session_state');
      url.searchParams.delete('iss'); // Remove issuer parameter to prevent redirect loops
      url.hash = ''; // Clear hash fragment entirely
      window.history.replaceState({}, document.title, url.toString());

      // Set a flag to prevent initKeycloak from proceeding
      initializationFailed = true;
    } else {
      // Check if we're reusing the same Keycloak session across multiple OAuth attempts
      // But only after we've tried the same code more than once
      const lastSessionState = sessionStorage.getItem('last_keycloak_session_state');
      const codeAttemptCount = sessionStorage.getItem(`code_attempts_${oauthCode}`) || '0';
      const attempts = parseInt(codeAttemptCount, 10);

      if (lastSessionState && sessionState && lastSessionState === sessionState && attempts > 1) {
        console.error('❌ Module load: Detected loop - same Keycloak session reused multiple times!');
        console.error('❌ Session state:', sessionState.substring(0, 8) + '...');
        console.error('❌ Attempts:', attempts);
        console.error('❌ This indicates a stale Keycloak server session with invalid nonce');
        console.error('❌ Forcing complete logout to destroy server session...');

        // Clear all session storage
        sessionStorage.clear();

        // Force logout from Keycloak to destroy the stale server session
        const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL || 'https://keycloak.global.gdm.rangeos/auth';
        const realm = import.meta.env.VITE_KEYCLOAK_REALM || 'cloudcents';
        const logoutUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout?redirect_uri=${encodeURIComponent(window.location.origin)}`;
        window.location.href = logoutUrl;
        return; // Stop execution
      }

      // Track attempt count for this code
      sessionStorage.setItem(`code_attempts_${oauthCode}`, (attempts + 1).toString());

      // Track this session state to detect loops
      if (sessionState) {
        sessionStorage.setItem('last_keycloak_session_state', sessionState);
      }

      console.log('📝 Module load: Detected new OAuth code - will attempt backend exchange');
      // Don't add to attemptedOAuthCodes yet - let the backend token exchange logic handle that
    }
  } else {
    console.log('ℹ️ Module load: No OAuth code in URL (normal first visit or after error)');
    // Don't clear sessionStorage here - keycloak.init() needs to store nonce before we return
  }
})();

/**
 * Set the auth token as a cookie for SSE endpoints
 */
const setAuthCookie = async (token: string): Promise<void> => {
  if (!token) {
    console.warn('Cannot set auth cookie: token is empty');
    return;
  }

  try {
    const response = await fetch('/api/auth/set-cookie', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
      credentials: 'include', // Important: include cookies
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('Failed to set auth cookie:', response.status, errorText);
    } else {
      console.debug('✓ Auth cookie set successfully');
    }
  } catch (error) {
    console.error('Error setting auth cookie:', error);
  }
};

/**
 * Fetch configuration from the backend
 */
const fetchConfig = async (): Promise<{ keycloak: { url: string; realm: string; clientId: string }; apiUrl: string }> => {
  try {
    const response = await fetch('/api/config');
    if (!response.ok) {
      throw new Error(`Failed to fetch config: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching config from backend, falling back to build-time env vars:', error);
    // Fallback to build-time environment variables
    return {
      keycloak: {
        url: import.meta.env.VITE_KEYCLOAK_URL || '',
        realm: import.meta.env.VITE_KEYCLOAK_REALM || '',
        clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || '',
      },
      apiUrl: import.meta.env.VITE_API_URL || '',
    };
  }
};

export const initKeycloak = async (): Promise<typeof keycloakInstance> => {
  // Return existing instance if already initialized
  if (keycloakInstance) {
    console.log('🔐 Keycloak already initialized, returning existing instance');
    return keycloakInstance;
  }

  // The module-load code already handles detecting and cleaning up reused OAuth codes
  // If we get here, we can proceed with normal initialization

  // If initialization has already failed, don't retry to prevent loops
  if (initializationFailed) {
    console.error('❌ Keycloak initialization previously failed, not retrying');
    throw new Error('Keycloak initialization failed previously. Please reload the page.');
  }

  // Prevent concurrent initialization attempts that cause redirect loops
  if (isInitializing) {
    console.warn('⚠️ Keycloak initialization already in progress, waiting...');
    // Wait for the other initialization to complete
    while (isInitializing) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    if (keycloakInstance) {
      return keycloakInstance;
    }
    if (initializationFailed) {
      throw new Error('Keycloak initialization failed');
    }
  }

  isInitializing = true;
  
  try {
    // Check if we previously failed role validation to prevent infinite loop
    const roleCheckFailed = sessionStorage.getItem('role_check_failed');
    if (roleCheckFailed === 'true') {
      console.error('❌ Previous authentication failed due to missing role');
      throw new Error('Access denied: You do not have the required content-administrator role to access this application.');
    }

    // Fetch configuration from backend (runtime config)
    const config = await fetchConfig();
    const { url: keycloakUrl, realm, clientId } = config.keycloak;

    console.log('🔐 Initializing Keycloak...');
    console.log('  URL:', keycloakUrl);
    console.log('  Realm:', realm);
    console.log('  Client ID:', clientId);

    if (!keycloakUrl || !realm || !clientId) {
      throw new Error('Keycloak configuration is incomplete. Please check your environment variables.');
    }

    keycloakInstance = new Keycloak({
      url: keycloakUrl,
      realm: realm,
      clientId: clientId,
    });

    // Add error handler to catch initialization errors
    let initError: any = null;
    keycloakInstance.onAuthError = (error: any) => {
      console.error('🔴 Keycloak onAuthError:', error);
      initError = error;
    };

    keycloakInstance.onAuthRefreshError = () => {
      console.error('🔴 Keycloak onAuthRefreshError');
    };

    // Check if we have an OAuth code in the URL - if so, exchange it via backend
    // (needed for confidential clients to send client_secret from backend)
    const urlParams = new URLSearchParams(window.location.search);
    const oauthCode = urlParams.get('code');

    if (oauthCode && !attemptedOAuthCodes.has(oauthCode)) {
      console.log('🔄 Detected OAuth code - exchanging via backend endpoint');
      attemptedOAuthCodes.add(oauthCode);
      saveAttemptedCodes(attemptedOAuthCodes);

      try {
        // Debug: Check all sessionStorage and localStorage for PKCE verifier
        console.log('  🔍 All sessionStorage keys:');
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key) {
            const value = sessionStorage.getItem(key);
            console.log(`    session[${key}]:`, value?.substring(0, 100));
          }
        }
        console.log('  🔍 All localStorage keys:');
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key) {
            const value = localStorage.getItem(key);
            console.log(`    local[${key}]:`, value?.substring(0, 100));
          }
        }

        // Try to find code_verifier and redirect_uri from Keycloak callback data
        const state = urlParams.get('state');
        let codeVerifier: string | null = null;
        let redirectUri: string | null = null;

        // Keycloak.js 26+ stores PKCE data in localStorage under 'kc-callback-{state}'
        if (state) {
          const callbackKey = `kc-callback-${state}`;
          const callbackData = localStorage.getItem(callbackKey);
          console.log(`  Checking ${callbackKey}:`, !!callbackData);
          if (callbackData) {
            try {
              const parsed = JSON.parse(callbackData);
              console.log('  Parsed callback data keys:', Object.keys(parsed));
              codeVerifier = parsed.pkceCodeVerifier || parsed.codeVerifier || parsed.code_verifier;
              redirectUri = parsed.redirectUri || parsed.redirect_uri;
            } catch (e) {
              console.warn('  Failed to parse callback data:', e);
            }
          }
        }

        // Fallback to other possible locations for code_verifier
        if (!codeVerifier) {
          codeVerifier =
            sessionStorage.getItem('kc-pkce-code-verifier') ||
            sessionStorage.getItem(`pkce-code-verifier-${state}`) ||
            sessionStorage.getItem('pkce-code-verifier') ||
            localStorage.getItem('kc-pkce-code-verifier') ||
            localStorage.getItem(`pkce-code-verifier-${state}`) ||
            localStorage.getItem('pkce-code-verifier');
        }

        // Fallback redirect_uri if not found in callback data
        if (!redirectUri) {
          redirectUri = window.location.origin + window.location.pathname;
        }

        console.log('  Code verifier found:', !!codeVerifier);
        if (codeVerifier) {
          console.log('  Code verifier preview:', codeVerifier.substring(0, 20) + '...');
        }
        console.log('  Redirect URI:', redirectUri);

        // Exchange code via backend
        const apiUrl = import.meta.env.VITE_API_URL || window.location.origin + '/api';
        console.log('  API URL:', apiUrl);
        const response = await fetch(`${apiUrl}/auth/token-exchange`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: oauthCode,
            redirectUri,
            codeVerifier: codeVerifier || undefined,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.details || error.error || 'Token exchange failed');
        }

        const tokens = await response.json();
        console.log('✅ Backend token exchange successful');

        // Helper function to decode JWT token
        const decodeToken = (token: string) => {
          try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(c =>
              '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            ).join(''));
            return JSON.parse(jsonPayload);
          } catch (e) {
            console.error('Failed to decode token:', e);
            return undefined;
          }
        };

        // Manually set the token in Keycloak instance
        keycloakInstance.token = tokens.access_token;
        keycloakInstance.refreshToken = tokens.refresh_token;
        keycloakInstance.idToken = tokens.id_token;
        keycloakInstance.tokenParsed = decodeToken(tokens.access_token);
        keycloakInstance.refreshTokenParsed = tokens.refresh_token ? decodeToken(tokens.refresh_token) : undefined;
        keycloakInstance.idTokenParsed = tokens.id_token ? decodeToken(tokens.id_token) : undefined;

        // Set timeSkew for token expiration calculations
        keycloakInstance.timeSkew = 0;

        // Clean up URL
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('code');
        cleanUrl.searchParams.delete('state');
        cleanUrl.searchParams.delete('session_state');
        cleanUrl.searchParams.delete('iss'); // Remove issuer parameter to prevent redirect loops
        window.history.replaceState({}, document.title, cleanUrl.toString());

        // Mark as authenticated
        keycloakInstance.authenticated = true;

        // Set global token for integrations API
        (window as any).__keycloakToken__ = keycloakInstance.token;

        // Set auth cookie for SSE endpoints
        await setAuthCookie(keycloakInstance.token);

        // Setup token refresh interval
        setInterval(async () => {
          try {
            // Check if token is about to expire (within 30 seconds)
            const tokenExp = keycloakInstance.tokenParsed?.exp || 0;
            const now = Math.floor(Date.now() / 1000);
            const timeLeft = tokenExp - now;

            if (timeLeft < 30 && keycloakInstance.refreshToken) {
              console.log('🔄 Token expiring soon, refreshing via backend...');

              // Exchange refresh token via backend
              const refreshResponse = await fetch(`${apiUrl}/auth/token-refresh`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  refreshToken: keycloakInstance.refreshToken,
                }),
              });

              if (refreshResponse.ok) {
                const newTokens = await refreshResponse.json();
                keycloakInstance.token = newTokens.access_token;
                keycloakInstance.refreshToken = newTokens.refresh_token;
                keycloakInstance.idToken = newTokens.id_token;
                keycloakInstance.tokenParsed = decodeToken(newTokens.access_token);
                keycloakInstance.refreshTokenParsed = newTokens.refresh_token ? decodeToken(newTokens.refresh_token) : undefined;
                keycloakInstance.idTokenParsed = newTokens.id_token ? decodeToken(newTokens.id_token) : undefined;

                // Update cookie and global token
                await setAuthCookie(keycloakInstance.token);
                (window as any).__keycloakToken__ = keycloakInstance.token;

                console.log('✅ Token refreshed via backend');
              } else {
                console.warn('⚠️ Token refresh failed, will redirect to login');
                keycloakInstance.login();
              }
            }
          } catch (error) {
            console.error('❌ Token refresh error:', error);
          }
        }, 10000); // Check every 10 seconds

        console.log('🔍 Keycloak manually authenticated with backend tokens');
        return keycloakInstance;
      } catch (error) {
        console.error('❌ Backend token exchange failed:', error);
        // Fall through to normal init which will fail and trigger re-login
      }
    }

    const authenticated = await keycloakInstance.init({
      onLoad: 'check-sso', // Check authentication status without auto-redirect
      checkLoginIframe: false, // Disable to avoid issues with older Keycloak
      enableLogging: true, // Enable Keycloak debug logs
      timeSkew: 60, // Allow up to 60 seconds of clock skew between browser and server
      // NO pkceMethod - confidential client uses client_secret only (no PKCE)
      flow: 'standard', // Explicit standard flow (authorization code)
      responseMode: 'query', // Try query mode instead of fragment
    }).catch((error: any) => {
      console.error('🔴 Keycloak init promise rejected:', error);
      throw error || initError || new Error('Keycloak authentication failed');
    });

    // If not authenticated, manually redirect to login (only once)
    if (!authenticated) {
      console.log('🔐 Not authenticated, redirecting to login...');
      await keycloakInstance.login();
      return keycloakInstance; // This line won't be reached due to redirect
    }
    
    console.log('🔍 Keycloak init result - authenticated:', authenticated);
    console.log('🔍 Token exists after init:', !!keycloakInstance.token);
    
    // If authenticated but no token, try multiple refresh attempts
    if (authenticated && !keycloakInstance.token) {
      console.log('⚠️ Authenticated but no token, attempting to refresh...');
      
      let attempts = 0;
      const maxAttempts = 3;
      
      while (!keycloakInstance.token && attempts < maxAttempts) {
        attempts++;
        console.log(`📡 Refresh attempt ${attempts}/${maxAttempts}...`);
        
        try {
          await keycloakInstance.updateToken(-1); // Force immediate refresh
          
          if (keycloakInstance.token) {
            console.log('✅ Token obtained after refresh attempt', attempts);
            break;
          } else {
            console.warn(`⚠️ Attempt ${attempts} succeeded but still no token`);
            // Wait a bit before next attempt
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        } catch (refreshError) {
          console.error(`❌ Refresh attempt ${attempts} failed:`, refreshError);
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      // Final check
      if (!keycloakInstance.token) {
        console.error('❌ Failed to obtain token after', attempts, 'attempts');
        console.error('💡 This usually means:');
        console.error('   1. Keycloak client configuration issue');
        console.error('   2. Network/CORS issue');
        console.error('   3. Session expired - try clearing cookies and reloading');
        
        throw new Error('Authentication succeeded but failed to obtain access token. Please clear your browser cache and try again.');
      }
    }

    if (authenticated) {
      console.log('✓ User authenticated');
      console.log('🔍 Debug - keycloakInstance.token exists?', !!keycloakInstance.token);
      console.log('🔍 Debug - keycloakInstance.token length:', keycloakInstance.token?.length);
      console.log('🔍 Debug - keycloakInstance.tokenParsed exists?', !!keycloakInstance.tokenParsed);
      
      // Try manual token parsing if tokenParsed is undefined
      let tokenParsed = keycloakInstance.tokenParsed as any;
      
      if (!tokenParsed && keycloakInstance.token) {
        console.log('⚠️ Token exists but tokenParsed is undefined, manually parsing...');
        try {
          // Manually decode the JWT token
          const base64Url = keycloakInstance.token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          tokenParsed = JSON.parse(jsonPayload);
          console.log('✓ Manually parsed token successfully');
        } catch (error) {
          console.error('❌ Failed to manually parse token:', error);
        }
      }
      
      console.log('✓ User:', tokenParsed?.preferred_username || 'unknown');
      console.log('✓ Token valid until:', new Date((tokenParsed?.exp || 0) * 1000));
      
      // Debug: Log FULL token structure for debugging
      console.log('🔍 Checking for content-administrator role...');
      console.log('📋 FULL TOKEN STRUCTURE:', JSON.stringify(tokenParsed, null, 2));
      console.log('  realm_access.roles:', tokenParsed?.realm_access?.roles);
      console.log('  resource_access:', Object.keys(tokenParsed?.resource_access || {}));
      console.log('  resource_access[s3-content-manager]:', tokenParsed?.resource_access?.['s3-content-manager']?.roles);
      console.log('  resource_access[rangeos-moodle]:', tokenParsed?.resource_access?.['rangeos-moodle']?.roles);
      console.log('  top-level roles:', tokenParsed?.roles);
      
      const hasRole = 
        tokenParsed?.realm_access?.roles?.includes('content-administrator') ||
        tokenParsed?.resource_access?.['s3-content-manager']?.roles?.includes('content-administrator') ||
        tokenParsed?.resource_access?.['rangeos-moodle']?.roles?.includes('content-administrator') ||
        tokenParsed?.roles?.includes('content-administrator');
      
      if (!hasRole) {
        console.error('❌ User does not have content-administrator role in any expected location');
        
        // Mark that role check failed to prevent re-authentication loop
        sessionStorage.setItem('role_check_failed', 'true');
        
        // Clear tokens to prevent auto-login
        keycloakInstance.clearToken();
        
        throw new Error('Access denied: You do not have the required content-administrator role to access this application.');
      }
      
      // Clear any previous role check failure flag since user now has the role
      sessionStorage.removeItem('role_check_failed');
      console.log('✓ User has content-administrator role');

      // Clean OAuth parameters from URL after successful authentication
      // This prevents loop detection from triggering on page reload
      // OAuth parameters can be in EITHER query string OR hash fragment
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const hasOAuthParams = urlParams.has('code') || urlParams.has('state') || urlParams.has('session_state') ||
                             hashParams.has('code') || hashParams.has('state') || hashParams.has('session_state');

      if (hasOAuthParams) {
        console.log('🧹 Cleaning OAuth parameters from URL after successful authentication');
        const cleanUrl = new URL(window.location.href);

        // Clean query string parameters
        cleanUrl.searchParams.delete('code');
        cleanUrl.searchParams.delete('state');
        cleanUrl.searchParams.delete('session_state');
        cleanUrl.searchParams.delete('iss');

        // Clean hash fragment parameters
        if (cleanUrl.hash) {
          const cleanHashParams = new URLSearchParams(cleanUrl.hash.substring(1));
          cleanHashParams.delete('code');
          cleanHashParams.delete('state');
          cleanHashParams.delete('session_state');
          cleanHashParams.delete('iss');

          // Rebuild hash or remove it if empty
          const cleanHashString = cleanHashParams.toString();
          cleanUrl.hash = cleanHashString ? `#${cleanHashString}` : '';
        }

        window.history.replaceState({}, document.title, cleanUrl.toString());

        // Clear session state tracking to prevent false positives on reload
        // This prevents loop detection from triggering if user uses browser back/forward
        sessionStorage.removeItem('last_keycloak_session_state');
        sessionStorage.removeItem('last_keycloak_oauth_code');
      }

      // Set auth cookie for SSE endpoints
      await setAuthCookie(keycloakInstance.token);

      // Expose token on window for axios interceptors to use
      // This avoids circular dependency issues with dynamic imports
      (window as any).__keycloakToken__ = keycloakInstance.token;

      // Expose clear auth helper in development for debugging
      (window as any).clearAuth = clearAuthState;
      console.log('💡 Debug helper available: window.clearAuth() to reset auth state');
    } else {
      console.warn('⚠️ User not authenticated');
    }

    // Setup token refresh using backend endpoint (required for confidential clients)
    if (keycloakInstance.token) {
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin + '/api';

      // Helper function to decode JWT token
      const decodeToken = (token: string) => {
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(c =>
            '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          ).join(''));
          return JSON.parse(jsonPayload);
        } catch (e) {
          console.error('Failed to decode token:', e);
          return undefined;
        }
      };

      setInterval(async () => {
        try {
          // Check if token is about to expire (within 30 seconds)
          const tokenExp = keycloakInstance.tokenParsed?.exp || 0;
          const now = Math.floor(Date.now() / 1000);
          const timeLeft = tokenExp - now;

          if (timeLeft < 30 && keycloakInstance.refreshToken) {
            console.log('🔄 Token expiring soon, refreshing via backend...');

            // Exchange refresh token via backend (required for confidential clients)
            const refreshResponse = await fetch(`${apiUrl}/auth/token-refresh`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                refreshToken: keycloakInstance.refreshToken,
              }),
            });

            if (refreshResponse.ok) {
              const newTokens = await refreshResponse.json();
              keycloakInstance.token = newTokens.access_token;
              keycloakInstance.refreshToken = newTokens.refresh_token;
              keycloakInstance.idToken = newTokens.id_token;
              keycloakInstance.tokenParsed = decodeToken(newTokens.access_token);
              keycloakInstance.refreshTokenParsed = newTokens.refresh_token ? decodeToken(newTokens.refresh_token) : undefined;
              keycloakInstance.idTokenParsed = newTokens.id_token ? decodeToken(newTokens.id_token) : undefined;

              // Update cookie and global token
              await setAuthCookie(keycloakInstance.token);
              (window as any).__keycloakToken__ = keycloakInstance.token;

              console.log('✅ Token refreshed via backend');
            } else {
              console.warn('⚠️ Token refresh failed, will redirect to login');
              keycloakInstance.login();
            }
          }
        } catch (error) {
          console.error('❌ Token refresh error:', error);
        }
      }, 10000); // Check every 10 seconds
    }

    return keycloakInstance;
  } catch (error) {
    console.error('❌ Keycloak initialization error:', error);
    console.error('❌ Error type:', typeof error);
    console.error('❌ Error keys:', error ? Object.keys(error) : 'null');

    // Clean up OAuth code from URL to prevent reuse attempts
    const url = new URL(window.location.href);
    if (url.searchParams.has('code') || url.searchParams.has('state') || url.searchParams.has('session_state') || url.searchParams.has('iss')) {
      console.log('🧹 Cleaning OAuth parameters from URL');
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      url.searchParams.delete('session_state');
      url.searchParams.delete('iss'); // Remove issuer parameter to prevent redirect loops
      window.history.replaceState({}, document.title, url.toString());
    }

    initializationFailed = true; // Mark initialization as permanently failed
    keycloakInstance = null;

    // Provide a more helpful error message
    let errorMessage = 'Unknown error during Keycloak initialization';
    if (error instanceof Error) {
      errorMessage = error.message || error.toString();
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else if (error && typeof error === 'object') {
      // Try to extract useful information from the error object
      errorMessage = JSON.stringify(error);
    }

    console.error('❌ Final error message:', errorMessage);
    throw new Error(errorMessage);
  } finally {
    isInitializing = false; // Always reset the flag
  }
};

export const getKeycloak = () => {
  if (!keycloakInstance) {
    throw new Error('Keycloak not initialized');
  }
  return keycloakInstance;
};

export const getAuthToken = (): string => {
  const kc = getKeycloak();
  return kc.token || '';
};

export const getUsername = (): string => {
  const kc = getKeycloak();
  if (!kc || !kc.tokenParsed) {
    return 'User';
  }
  return (
    kc.tokenParsed?.preferred_username ||
    kc.tokenParsed?.name ||
    kc.tokenParsed?.given_name ||
    kc.tokenParsed?.email ||
    'User'
  );
};

export const logout = (): void => {
  const kc = getKeycloak();

  // Clear the role check flag
  sessionStorage.removeItem('role_check_failed');

  // Reset initialization flags to allow re-login
  initializationFailed = false;
  isInitializing = false;
  attemptedOAuthCodes.clear();
  sessionStorage.removeItem('attempted_oauth_codes');

  // Clear the keycloak instance
  keycloakInstance = null;

  kc.logout({ redirectUri: window.location.origin });
};

// Helper function to completely clear auth state (useful for debugging)
export const clearAuthState = (): void => {
  console.log('🧹 Clearing all authentication state...');

  // Clear storage
  sessionStorage.clear();
  localStorage.clear();

  // Clear cookies by setting them to expire
  document.cookie.split(";").forEach((c) => {
    document.cookie = c
      .replace(/^ +/, "")
      .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });

  // Clear keycloak instance
  if (keycloakInstance) {
    try {
      keycloakInstance.clearToken();
    } catch (e) {
      console.warn('Could not clear token:', e);
    }
  }
  keycloakInstance = null;

  // Reset initialization flags
  initializationFailed = false;
  isInitializing = false;
  attemptedOAuthCodes.clear();
  sessionStorage.removeItem('attempted_oauth_codes');

  console.log('✅ Auth state cleared. Reloading page...');
  window.location.reload();
};

export const hasRole = (role: string): boolean => {
  try {
    const kc = getKeycloak();
    const tokenParsed = kc.tokenParsed as any;
    
    return (
      tokenParsed?.realm_access?.roles?.includes(role) ||
      tokenParsed?.resource_access?.['s3-content-manager']?.roles?.includes(role) ||
      tokenParsed?.roles?.includes(role) ||
      false
    );
  } catch (error) {
    return false;
  }
};

export const getUserRoles = (): string[] => {
  try {
    const kc = getKeycloak();
    const tokenParsed = kc.tokenParsed as any;
    
    const realmRoles = tokenParsed?.realm_access?.roles || [];
    const clientRoles = tokenParsed?.resource_access?.['s3-content-manager']?.roles || [];
    const otherRoles = tokenParsed?.roles || [];
    
    return [...new Set([...realmRoles, ...clientRoles, ...otherRoles])];
  } catch (error) {
    return [];
  }
};
